export const KINDS = ['change', 'error'] as const
export type Kind = (typeof KINDS)[number]

export const SECTION_LABELS: Readonly<Record<string, string>> = {
  daily_card: 'Карта дня',
  daily_card_template: 'Описания арканов',
  daily_pick: 'Три карты',
  deck: 'Колоды',
  deck_card: 'Карты колод',
  deck_custom_card: 'Свои карты',
  deck_back: 'Рубашки',
  reading: 'Разборы',
  reading_topic: 'Темы разборов',
  training_example: 'Студия',
  prompt_draft: 'Черновики промптов',
  card_ratings: 'Отклик',
  stats: 'Обзор',
  lunar_payment: 'Платежи',
  user: 'Пользователи',
  users: 'Пользователи',
}

export const SECTIONS = Object.keys(SECTION_LABELS).filter((k) => k !== 'users')

const ACTIONS: Readonly<Record<string, string>> = {
  'POST /admin/daily-cards/draw': 'Черновик карты дня',
  'PUT /admin/daily-cards/:date/card': 'Смена карты дня',
  'PUT /admin/daily-cards/:date/interpretation': 'Правка трактовки карты дня',
  'POST /admin/daily-cards/:date/publish': 'Публикация карты дня',
  'PUT /admin/daily-card-templates/:number': 'Правка описания аркана',
  'POST /admin/daily-picks/draw': 'Черновик «Трёх карт»',
  'PUT /admin/daily-picks/:date/options/:position/interpretation': 'Правка трактовки «Трёх карт»',
  'POST /admin/daily-picks/:date/publish': 'Публикация «Трёх карт»',
  'PATCH /users/:id/role': 'Смена роли',
  'POST /auth/register': 'Создание пользователя',
  'POST /admin/users/:id/block': 'Блокировка пользователя',
  'POST /admin/lunars/payments/:id/refund': 'Возврат платежа',
  'POST /admin/users/:id/unblock': 'Разблокировка пользователя',
  'POST /admin/users/:id/revoke-sessions': 'Сброс сессий',
  'POST /admin/users/:id/merge': 'Слияние аккаунтов',
  'POST /admin/decks': 'Создание колоды',
  'PATCH /admin/decks/:deckId': 'Правка колоды',
  'DELETE /admin/decks/:deckId': 'Удаление колоды',
  'PUT /admin/decks/:deckId/cards/:cardId': 'Загрузка карты в колоду',
  'DELETE /admin/decks/:deckId/cards/:cardId': 'Удаление карты из колоды',
  'PUT /admin/decks/:deckId/back': 'Смена рубашки',
  'POST /admin/decks/:deckId/custom-cards': 'Новая своя карта',
  'PATCH /admin/decks/:deckId/custom-cards/:cardId': 'Правка своей карты',
  'PUT /admin/decks/:deckId/custom-cards/:cardId/image': 'Картинка своей карты',
  'DELETE /admin/deck-backs/:backId': 'Удаление рубашки',
  'POST /admin/reading-topics': 'Новая тема разборов',
  'PUT /admin/reading-topics/order': 'Порядок тем разборов',
  'PATCH /admin/reading-topics/:slug': 'Правка темы разборов',
  'DELETE /admin/reading-topics/:slug': 'Удаление темы разборов',
  'PUT /admin/arcana-readings/:id': 'Правка разбора',
  'POST /admin/arcana-readings/:id/start': 'Разбор взят в работу',
  'POST /admin/arcana-readings/:id/messages': 'Сообщение в разборе',
  'POST /admin/arcana-readings/:id/voice': 'Голосовое в разборе',
  'PUT /admin/arcana-readings/:id/messages/:messageId/reaction': 'Реакция в разборе',
  'DELETE /admin/arcana-readings/:id/messages/:messageId/reaction': 'Снятие реакции в разборе',
  'POST /trainer/attempts': 'Попытка в тренажёре',
  'PATCH /trainer/attempts/:id': 'Правка ответа в тренажёре',
  'POST /trainer/attempts/:id/rating': 'Оценка ответа',
  'POST /trainer/attempts/:id/approve': 'Одобрение примера',
  'POST /trainer/attempts/:id/reject': 'Отклонение примера',
  'PATCH /trainer/fewshot/:id': 'Эталон: закрепить или исключить',
  'POST /trainer/examples': 'Новый эталон',
  'POST /trainer/chat/examples': 'Эталон из чата',
  'POST /trainer/prompt-drafts': 'Новый черновик промпта',
  'PATCH /trainer/prompt-drafts/:id': 'Правка черновика промпта',
  'DELETE /trainer/prompt-drafts/:id': 'Удаление черновика промпта',
}

const METHOD_FALLBACK: Readonly<Record<string, string>> = {
  POST: 'Создание',
  PUT: 'Изменение',
  PATCH: 'Изменение',
  DELETE: 'Удаление',
  GET: 'Запрос',
}

export function actionLabel(method: string, route: string): string {
  return ACTIONS[`${method} ${route}`] ?? METHOD_FALLBACK[method] ?? method
}

export const FIELD_LABELS: Readonly<Record<string, string>> = {
  status: 'Статус',
  card_id: 'Карта',
  interpretation: 'Трактовка',
  headline: 'Заголовок',
  body: 'Описание',
  attention: 'На что обратить внимание',
  advice: 'Совет дня',
  question: 'Вопрос себе',
  role: 'Роль',
  blocked_at: 'Заблокирован',
  blocked_reason: 'Причина блокировки',
  reason: 'Причина',
  name: 'Название',
  description: 'Описание',
  kind: 'Тип',
  enabled: 'Включено',
  is_default: 'Основная',
  back_id: 'Рубашка',
  image_hash: 'Изображение',
  archived_at: 'В архиве',
  deleted_at: 'Удалено',
  title: 'Заголовок',
  hint: 'Подсказка',
  sort_order: 'Порядок',
  slugs: 'Порядок тем',
  slug: 'Код',
  expert_id: 'Таролог',
  clarifications_left: 'Уточнений осталось',
  format: 'Формат',
  upgrade_offered: 'Предложена консультация',
  rating: 'Оценка',
  comment: 'Комментарий',
  expert_output: 'Ответ эксперта',
  pinned: 'Закреплён',
  excluded: 'Исключён',
  base_version: 'Базовая версия',
  content_chars: 'Длина промпта, символов',
  content: 'Текст',
  text: 'Текст',
  file: 'Файл',
}

const VALUE_LABELS: Readonly<Record<string, string>> = {
  draft: 'черновик',
  published: 'опубликовано',
  pending: 'ждёт',
  in_progress: 'в работе',
  clarifying: 'ждёт ответа',
  ready: 'готов',
  closed: 'закрыт',
  rejected: 'отклонён',
  approved: 'одобрен',
  daily_card: 'для карты дня',
  three_cards: 'для 3 карт',
  guest: 'гость',
  user: 'пользователь',
  expert: 'эксперт',
  admin: 'администратор',
}

export const ROLE_LABELS: Readonly<Record<string, string>> = {
  admin: 'Администратор',
  expert: 'Эксперт',
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'да' : 'нет'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    if (ISO_RE.test(value)) return new Date(value).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })
    return VALUE_LABELS[value] ?? value
  }
  if (Array.isArray(value)) return value.map(formatValue).join(', ')
  if (isRecord(value)) {
    if (typeof value.chars === 'number') return `текст, ${value.chars.toLocaleString('ru-RU')} символов`
    if (typeof value.bytes === 'number') {
      const kb = Math.max(1, Math.round(value.bytes / 1024))
      return `${typeof value.type === 'string' && value.type ? `${value.type}, ` : ''}${kb.toLocaleString('ru-RU')} КБ`
    }
    if (typeof value.items === 'number') return `${value.items} элементов`
    return JSON.stringify(value)
  }
  return String(value)
}
